import { Contact } from "lucide-react";
import { EntityLink, EntityLinkPlain } from "./EntityLink";

/**
 * ContactLink — canonical "First Last (internalId)" rendering of a family
 * contact, linking to the contact detail page.
 *
 * Accepts either a pre-combined `name` or separate `firstName`/`lastName`.
 * The `(internalId)` suffix is shown whenever `contactInternalId` is present
 * (set `showId={false}` to suppress). Falls back to plain text when
 * `contactId` is missing.
 */
export interface ContactLinkProps {
  contactId?: string | null;
  contactInternalId?: string | null;
  /** Full name if already combined upstream. */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  showId?: boolean;
  className?: string;
  "data-testid"?: string;
}

function formatContactLabel({
  contactInternalId,
  name,
  firstName,
  lastName,
  showId = true,
}: ContactLinkProps): string {
  const fullName = (name ?? [firstName, lastName].filter(Boolean).join(" ")).trim();
  const base = fullName || "—";
  if (showId && contactInternalId) {
    return `${base} (${contactInternalId})`;
  }
  return base;
}

export function ContactLink(props: ContactLinkProps) {
  const { contactId, className, "data-testid": dataTestId } = props;
  const label = formatContactLabel(props);
  if (!contactId) {
    return (
      <EntityLinkPlain
        icon={Contact}
        title={label}
        className={className}
        data-testid={dataTestId}
      >
        {label}
      </EntityLinkPlain>
    );
  }
  return (
    <EntityLink
      to={`/contacts/${contactId}/edit`}
      icon={Contact}
      title={label}
      className={className}
      data-testid={dataTestId}
    >
      {label}
    </EntityLink>
  );
}
